// kfe-completion-screen.js — shared exercise-completion footer.
//
// Brief 046 introduced this component and wired it into all seven exercise
// types. Brief 049 restores iOS-canonical, PER-EXERCISE button sets: the
// component is the shared rendering primitive, but the labels, count, order,
// and any body prose are CONFIGURATION the host passes in — not hard-coded
// here. (Brief 046 had collapsed all seven to one "Redo / Return to lesson"
// pair; that violated CLAUDE.md Rule #9 — iOS is canonical, including its
// inconsistency across exercise types. HAAI has three buttons, Self-Recording
// uses Yes/No with body prose, L2B Training uses "Practice some more / Go back
// to the menu", etc. — each host now passes its own.)
//
// Properties:
//   message: string            — headline (falls back to "exercise.complete")
//   prompt:  string            — optional body prose shown above the buttons
//                                (e.g. Self-Recording's "…practice one more
//                                time?", L2B Testing's "Do the Test Again?")
//   actions: Array<{ label: string, eventName: string, primary?: boolean }>
//                              — one button per entry, rendered in order; each
//                                dispatches its `eventName` as a non-bubbling
//                                CustomEvent on tap.
//   layout:  "row" | "column"  — button arrangement. iOS stacks HAAI's three
//                                buttons and L2B's pair vertically; Self-
//                                Recording / Multiple Choice are side-by-side.
//                                Defaults to "row".
//
// The host supplies its score/verdict summary (if any) via the default slot,
// which renders between the headline and the prompt/actions.
//
// Usage from an exercise screen's render():
//
//   const screen = document.createElement("kfe-completion-screen");
//   screen.message = t("haai.title");
//   screen.layout = "column";
//   screen.actions = [
//     { label: t("completion.haai.reviewPairs"), eventName: "kfe:haai:reviewPairs" },
//     { label: t("completion.haai.redoTest"),    eventName: "kfe:haai:redoTest", primary: true },
//     { label: t("completion.haai.leaveTest"),   eventName: "kfe:haai:leaveTest" },
//   ];
//   screen.addEventListener("kfe:haai:reviewPairs", () => this._onReviewPairs());
//   screen.addEventListener("kfe:haai:redoTest",    () => this._onRedo());
//   screen.addEventListener("kfe:haai:leaveTest",   () => this._onReturnToLesson());

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";

/**
 * @typedef {object} CompletionAction
 * @property {string}  label
 * @property {string}  eventName
 * @property {boolean} [primary]
 */

export class KFECompletionScreen extends KFEComponent {
  /** @type {string[]} */
  static props = ["message", "prompt", "actions", "layout"];

  static styles = `
    :host { display: block; padding: 1.5rem; text-align: center; }
    .message { font-size: var(--kfe-h2, 1.5rem); font-weight: 600;
               color: var(--kfe-fg); margin: 0 0 1.25rem; }
    .extra { margin-bottom: 1rem; }
    .prompt { font-size: 1rem; color: var(--kfe-fg); margin: 0 0 1.25rem;
              line-height: 1.4; }
    .actions { display: flex; gap: 0.75rem; justify-content: center;
               flex-wrap: wrap; }
    .actions.column { flex-direction: column; align-items: stretch; }
    button { padding: 0.6rem 1.25rem; border-radius: 8px;
             font-size: 1rem; cursor: pointer; }
    button.primary { background: var(--kfe-accent); color: var(--kfe-on-accent);
                     border: 0; }
    button.secondary { background: transparent; color: var(--kfe-fg);
                       border: 1px solid var(--kfe-border); }
    button:focus-visible { outline: 2px solid var(--kfe-accent);
                           outline-offset: 2px; }
  `;

  render() {
    /** @type {any} */ const self = this;
    const message = typeof self.message === "string" && self.message.length > 0
      ? self.message
      : t("exercise.complete");

    /** @type {CompletionAction[]} */
    const actions = Array.isArray(self.actions) ? self.actions : [];

    const actionsRow = document.createElement("div");
    actionsRow.className = "actions" + (self.layout === "column" ? " column" : "");
    for (const action of actions) {
      if (!action || typeof action.label !== "string") continue;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = action.primary ? "primary" : "secondary";
      btn.textContent = action.label;
      if (action.eventName) {
        btn.dataset.event = action.eventName;
        // Capture eventName per-iteration; the listener is wired once per
        // render (the base class replaces the shadow tree wholesale, so old
        // listeners are discarded with their nodes — no stacking).
        const name = action.eventName;
        btn.addEventListener("click", () => {
          this.dispatchEvent(new CustomEvent(name));
        });
      }
      actionsRow.appendChild(btn);
    }

    const extra = document.createElement("div");
    extra.className = "extra";
    extra.appendChild(document.createElement("slot"));

    let promptEl = /** @type {HTMLElement | string} */ ("");
    if (typeof self.prompt === "string" && self.prompt.length > 0) {
      const p = document.createElement("p");
      p.className = "prompt";
      p.textContent = self.prompt;
      promptEl = p;
    }

    return html`
      <p class="message">${message}</p>
      ${extra}
      ${promptEl}
      ${actionsRow}
    `;
  }
}
